import { FormEvent, useState } from 'react';

type Errors = {
  name?: string;
  email?: string;
  message?: string;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Public by design. This gets baked into the JS bundle, which is fine because
// the endpoint is meant to be called by visitors. It is not a secret, which is
// exactly why the real validation lives in the Lambda and not here.
const ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT ?? '';

export const Contact = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  // Hidden from humans. Bots fill every field they find, which is the tell.
  const [website, setWebsite] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState('');
  const [failed, setFailed] = useState(false);
  const [sending, setSending] = useState(false);

  const validate = (): Errors => {
    const next: Errors = {};
    if (!name.trim()) next.name = 'A name helps me reply properly.';
    if (!EMAIL_RE.test(email.trim())) {
      next.email = email.trim()
        ? 'That address looks incomplete.'
        : 'I need somewhere to reply.';
    }
    if (message.trim().length < 12) {
      next.message = 'A sentence or two would help.';
    }
    return next;
  };

  const onEdit = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setStatus('');
    setFailed(false);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (sending) return;

    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setStatus('');
      setFailed(false);
      return;
    }

    if (!ENDPOINT) {
      setFailed(true);
      setStatus('Contact form is not configured yet.');
      return;
    }

    setSending(true);
    setStatus('');
    setFailed(false);

    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          website,
        }),
      });

      if (!res.ok) throw new Error(`Request failed with ${res.status}`);

      setStatus('Thanks. I will get back to you soon.');
      setName('');
      setEmail('');
      setMessage('');
    } catch {
      // Keep whatever they typed. Losing a written message on a failed send is
      // the worst possible outcome here.
      setFailed(true);
      setStatus(
        'That did not send. Try again, or email me directly if it keeps failing.'
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="contact-inner">
        <div className="reveal">
          <p className="section-eyebrow">05 — Contact</p>
          <h2 className="contact-title">Get in touch.</h2>
          <p className="contact-copy">
            If you are hiring for a software engineering role, I would
            like to hear from you. I read every message myself.
          </p>
          {/* Deliberately not a link. The profile carries a real name, and
              this site is published under an alias, so the URL stays out of
              the bundle entirely. Anything shipped to the browser is public no
              matter how it is dressed up, so the only way to keep it private
              is to not ship it. */}
          <div className="contact-linkedin">
            <span className="contact-linkedin-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false">
                <path
                  fill="currentColor"
                  d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z"
                />
              </svg>
            </span>
            <div>
              <p className="contact-linkedin-title">LinkedIn</p>
              <p className="contact-linkedin-note">
                Shared on request, for recruiters and hiring conversations. Send
                a message using the form and I will reply with my profile.
              </p>
            </div>
          </div>

          <div className="contact-meta">
            <div>No trackers on this site. Analytics, if ever, will be privacy respecting.</div>
          </div>
        </div>

        <form className="contact-form reveal" onSubmit={handleSubmit} noValidate>
          <label className="field">
            <span>Name</span>
            <input
              className="input"
              name="name"
              type="text"
              value={name}
              onChange={(e) => onEdit(setName)(e.target.value)}
              placeholder="What should I call you?"
              disabled={sending}
            />
            <span className="field-error">{errors.name}</span>
          </label>

          <label className="field">
            <span>Email</span>
            <input
              className="input"
              name="email"
              type="email"
              value={email}
              onChange={(e) => onEdit(setEmail)(e.target.value)}
              placeholder="you@example.com"
              disabled={sending}
            />
            <span className="field-error">{errors.email}</span>
          </label>

          <label className="field">
            <span>Message</span>
            <textarea
              className="input"
              name="message"
              rows={4}
              value={message}
              onChange={(e) => onEdit(setMessage)(e.target.value)}
              placeholder="Role, team, or anything you'd like me to know."
              disabled={sending}
            />
            <span className="field-error">{errors.message}</span>
          </label>

          <div className="honeypot" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>

          <div className="contact-form-footer">
            <button className="btn btn-primary" type="submit" disabled={sending}>
              {sending ? 'Sending…' : 'Send message'}
            </button>
            <span
              className={failed ? 'contact-status is-error' : 'contact-status'}
              role="status"
              aria-live="polite"
            >
              {status}
            </span>
          </div>
        </form>
      </div>
    </section>
  );
};
